import { useState } from "react";
import type { FormEvent } from "react";

import { APIErrorException } from "@/core/services";
import { useDialogForm, useToast } from "@/ds";
import { useChaveIdempotencia, useConfirmarDescarte } from "@/shared/hooks";
import { cancelarTipoObra } from "@/modules/financeiro-obras";
import type { DestinoCancelamentoTipo, SucessorTipoObra } from "@/modules/financeiro-obras";
import { useCancelamentoTipoObraDados } from "../../../../hooks/use-planejamento-dados";
import { DESTINO_OBRIGATORIO } from "../campo-destino-cancelamento";
import { JUSTIFICATIVA_OBRIGATORIA } from "@/shared/components";
import {
  testIdsServicosMateriais,
  type ServicosMateriaisSecaoProps,
} from "../servicos-materiais-secao";
import { FEL_ZERO, felComContingencia } from "../planejamento-controle.cancelamento";
import { NO_VALUE } from "../planejamento-controle.constants";
import { formatMilhoes, formatMoeda } from "../planejamento-controle.format";
import {
  CANCELADO_MESSAGE,
  CANCELAR_TIPO_FORM_ID,
  CLASSIFICACAO_SEM_DADO,
  CONFIRMAR_EFETIVAR_LABEL,
  CONFIRMAR_LABEL,
  DESTINO_DEVOLVER_AJUDA,
  DESTINO_DEVOLVER_LABEL,
  DESTINO_REDISTRIBUIR_AJUDA,
  DESTINO_REDISTRIBUIR_CONTINGENCIA_AJUDA,
  DESTINO_REDISTRIBUIR_LABEL,
  DISTRIBUICAO_SEM_DADO,
  DOMINANTE_MESSAGE,
  DOMINANTE_SEM_SUCESSOR_MESSAGE,
  ENVIADO_PARA_APROVACAO,
  IMPACTO_DEVOLVER,
  IMPACTO_FEL_ZERO,
  IMPACTO_NEUTRO,
  IMPACTO_REDISTRIBUIR,
  IMPACTO_REDISTRIBUIR_CONTINGENCIA,
  SERVICOS_MATERIAIS_IMPEDIDO_TITLE,
  SERVICOS_MATERIAIS_TITLE,
  SUCESSOR_AJUDA,
  SUCESSOR_LABEL,
  SUCESSOR_PLACEHOLDER,
  SUCESSOR_DIFERENTE_AJUDA,
  SUCESSOR_OBRIGATORIO,
  SUCESSOR_SUGERIDO_SUFIXO,
  SUCESSOR_AJUDA_FEL_ZERO,
  SUCESSOR_UNICO_AJUDA,
  SUCESSOR_UNICO_AJUDA_FEL_ZERO,
  ULTIMO_TIPO_MESSAGE,
  ULTIMO_TIPO_PADRAO,
} from "./cancelar-tipo-obra-dialog.constants";
import type { DominanciaBlocoProps } from "../dominancia-bloco";
import type {
  CancelarTipoObraDialogProps,
  CancelarTipoObraEstado,
  EscopoAbrangidoLinha,
} from "./types-cancelar-tipo-obra-dialog";

type CancelarTipoForm = {
  justificativa: string;
  /** Vazio = a sugestão do servidor. */
  sucessorId: string;
  destino: DestinoCancelamentoTipo | "";
};

const rotulo = (item: { codigo: string; nome: string }) => `${item.codigo} · ${item.nome}`;

export function useCancelarTipoObraDialog({
  obraId,
  tipo,
  onClose,
  onConcluido,
}: CancelarTipoObraDialogProps) {
  const toast = useToast();
  const { comChave } = useChaveIdempotencia();
  const consulta = useCancelamentoTipoObraDados(obraId, tipo.obraTipoObraId);
  const dados = consulta.data;

  const [ultimoTipoPeloServidor, setUltimoTipoPeloServidor] = useState(false);

  let estado: CancelarTipoObraEstado = "pronto";
  if (consulta.status === "forbidden") estado = "proibido";
  else if (consulta.status === "error") estado = "erro";
  else if (consulta.status === "loading" || !dados) estado = "carregando";
  else if (dados.ehUltimoTipo || ultimoTipoPeloServidor) estado = "ultimo-tipo";

  const candidatos: SucessorTipoObra[] = dados?.candidatosSucessor ?? [];
  const sugerido = dados?.sucessorSugerido ?? null;
  const dominante = dados?.ehDominante ?? false;
  const exigeSucessor = dominante && candidatos.length > 0;

  /** Vazio no form = a sugestão do servidor; sem sugestão e sem escolha, continua vazio. */
  const sucessorEscolhido = (valores: CancelarTipoForm): string =>
    valores.sucessorId || sugerido?.obraTipoObraId || "";
  const escolheuOutro = (valores: CancelarTipoForm): boolean =>
    exigeSucessor && sucessorEscolhido(valores) !== sugerido?.obraTipoObraId;

  const efetivacaoImediata = tipo.fel === FEL_ZERO;
  const contingencia = felComContingencia(tipo.fel);
  const pedeDestino =
    estado === "pronto" && !efetivacaoImediata && (dados?.escoposAbrangidos.length ?? 0) > 0;

  const justificativaObrigatoria = (dados?.escoposAbrangidos.length ?? 0) > 0;

  const descarte = useConfirmarDescarte({ onDescartar: onClose, testId: "cancelar-tipo-descarte" });

  const form = useDialogForm<CancelarTipoForm>({
    initial: { justificativa: "", sucessorId: "", destino: "" },
    validate: (valores) => {
      const erros: Partial<Record<keyof CancelarTipoForm, string>> = {};
      if (justificativaObrigatoria && valores.justificativa.trim() === "")
        erros.justificativa = JUSTIFICATIVA_OBRIGATORIA;
      if (exigeSucessor && sucessorEscolhido(valores) === "")
        erros.sucessorId = SUCESSOR_OBRIGATORIO;
      if (pedeDestino && valores.destino === "") erros.destino = DESTINO_OBRIGATORIO;
      return erros;
    },
    onSubmit: async (valores) => {
      let resposta;
      try {
        const justificativa = valores.justificativa.trim();
        const body = {
          ...(justificativa ? { justificativa } : {}),
          ...(exigeSucessor ? { sucessorObraTipoObraId: sucessorEscolhido(valores) } : {}),
          ...(pedeDestino && valores.destino ? { destino: valores.destino } : {}),
        };
        resposta = await comChave(body, (idempotencyKey) =>
          cancelarTipoObra(obraId, tipo.obraTipoObraId, body, { idempotencyKey }),
        );
      } catch (erro) {
        if (
          erro instanceof APIErrorException &&
          erro.status === 409 &&
          ULTIMO_TIPO_PADRAO.test(erro.message)
        ) {
          setUltimoTipoPeloServidor(true);
          return;
        }
        throw erro;
      }
      const nome = rotulo(tipo);
      if (resposta.situacao === "APROVADA") toast.success(CANCELADO_MESSAGE(nome));
      else toast.info(ENVIADO_PARA_APROVACAO(nome));
      onConcluido();
    },
  });

  const { values } = form;
  const escolhido = sucessorEscolhido(values);
  const outro = escolheuOutro(values);

  const escopos: EscopoAbrangidoLinha[] = (dados?.escoposAbrangidos ?? []).map((escopo) => ({
    id: escopo.id,
    nome: escopo.nome,
    classificacao: tipo.classificacaoEscopos[escopo.id] ?? CLASSIFICACAO_SEM_DADO,
    distribuicao:
      [...new Set([escopo.distribuicao.servicos, escopo.distribuicao.materiais])]
        .filter((nome): nome is string => Boolean(nome))
        .join(" · ") || DISTRIBUICAO_SEM_DADO,
    total: formatMilhoes(escopo.totais?.total),
  }));
  const somaEscopos = (dados?.escoposAbrangidos ?? []).reduce(
    (soma, escopo) => soma + (escopo.totais?.total ?? 0),
    0,
  );
  const temValores = (dados?.escoposAbrangidos ?? []).some((escopo) => escopo.totais !== null);

  const atual = rotulo(tipo);
  const sucessaoAjuda = () => {
    if (candidatos.length === 1) {
      return efetivacaoImediata ? SUCESSOR_UNICO_AJUDA_FEL_ZERO : SUCESSOR_UNICO_AJUDA;
    }
    if (outro && sugerido) return SUCESSOR_DIFERENTE_AJUDA(rotulo(sugerido));
    return efetivacaoImediata ? SUCESSOR_AJUDA_FEL_ZERO : SUCESSOR_AJUDA;
  };
  let sucessao: DominanciaBlocoProps | null = null;
  if (dominante) {
    const escolhidoItem = candidatos.find((item) => item.obraTipoObraId === escolhido) ?? null;
    sucessao = {
      titulo: null,
      aviso: {
        texto: escolhidoItem
          ? DOMINANTE_MESSAGE(atual, rotulo(escolhidoItem))
          : DOMINANTE_SEM_SUCESSOR_MESSAGE(atual),
        testId: "cancelar-tipo-dominante",
      },
      cartoes: null,
      seletor: exigeSucessor
        ? {
            label: SUCESSOR_LABEL,
            placeholder: SUCESSOR_PLACEHOLDER,
            value: escolhido || null,
            opcoes: candidatos.map((item) => ({
              label: `${rotulo(item)}${item.obraTipoObraId === sugerido?.obraTipoObraId ? ` ${SUCESSOR_SUGERIDO_SUFIXO}` : ""}`,
              value: item.obraTipoObraId,
            })),
            ajuda: sucessaoAjuda(),
            error: form.errors.sucessorId ?? null,
            travado: candidatos.length === 1,
            onChange: (id: string) => form.setValue("sucessorId", id),
            testId: "cancelar-tipo-sucessor",
            errorTestId: "cancelar-tipo-sucessor-error",
          }
        : null,
      disabled: form.isSubmitting,
      testId: "cancelar-tipo-sucessao",
    };
  }

  const impedimentos = dados?.impedimentos ?? [];
  const impedido = impedimentos.length > 0;
  const servicosMateriais: ServicosMateriaisSecaoProps = {
    titulo: impedido ? SERVICOS_MATERIAIS_IMPEDIDO_TITLE : SERVICOS_MATERIAIS_TITLE,
    itens: dados?.servicosMateriais ?? [],
    impedimentos,
    testIds: testIdsServicosMateriais("cancelar-tipo"),
  };
  const confirmarDesabilitado = estado !== "pronto" || impedido;

  let avisoImpacto = IMPACTO_NEUTRO;
  if (efetivacaoImediata) avisoImpacto = IMPACTO_FEL_ZERO;
  else if (values.destino === "DEVOLVER") avisoImpacto = IMPACTO_DEVOLVER;
  else if (values.destino === "REDISTRIBUIR") {
    avisoImpacto = contingencia ? IMPACTO_REDISTRIBUIR_CONTINGENCIA : IMPACTO_REDISTRIBUIR;
  }

  return {
    formId: CANCELAR_TIPO_FORM_ID,
    estado,
    errorMessage: consulta.errorMessage,
    recarregar: consulta.reload,
    tipo: {
      codigo: tipo.codigo,
      titulo: `${tipo.codigo} - ${tipo.nome}`,
      classificacao: tipo.classificacao,
      detalhe: [
        `${escopos.length} ${escopos.length === 1 ? "escopo macro" : "escopos macro"}`,
        tipo.embarcacao,
        tipo.obra,
        tipo.centroCusto,
      ]
        .filter(Boolean)
        .join(" · "),
      valorTotal: tipo.valorTotal ?? (temValores ? formatMoeda(somaEscopos, "BRL") : null),
    },
    escopos,
    totalEscopos: temValores ? formatMilhoes(somaEscopos) : NO_VALUE,
    sucessao,
    confirmarLabel: efetivacaoImediata ? CONFIRMAR_EFETIVAR_LABEL : CONFIRMAR_LABEL,
    destino: pedeDestino
      ? {
          value: values.destino === "" ? null : values.destino,
          error: form.errors.destino ?? null,
          opcoes: [
            {
              valor: "DEVOLVER" as const,
              testId: "cancelar-tipo-destino-devolver",
              label: DESTINO_DEVOLVER_LABEL,
              ajuda: DESTINO_DEVOLVER_AJUDA,
            },
            {
              valor: "REDISTRIBUIR" as const,
              testId: "cancelar-tipo-destino-redistribuir",
              label: DESTINO_REDISTRIBUIR_LABEL,
              ajuda: contingencia
                ? DESTINO_REDISTRIBUIR_CONTINGENCIA_AJUDA
                : DESTINO_REDISTRIBUIR_AJUDA,
            },
          ],
        }
      : null,
    servicosMateriais,
    avisoImpacto,
    orientacaoUltimoTipo: estado === "ultimo-tipo" ? ULTIMO_TIPO_MESSAGE(atual, tipo.obra) : null,
    justificativa: values.justificativa,
    justificativaObrigatoria,
    justificativaError: form.errors.justificativa ?? null,
    isSubmitting: form.isSubmitting,
    submitError: form.submitError,
    confirmarDesabilitado,
    escolherDestino: (valor: DestinoCancelamentoTipo) => form.setValue("destino", valor),
    digitarJustificativa: (texto: string) => form.setValue("justificativa", texto),
    enviar: (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      void form.submit();
    },
    cancelar: () => descarte.pedir(form.isDirty),
    descarte: descarte.confirmacao,
  };
}
